import { Milestone } from "types/collaboration";
import { RequirementDetails } from "./requirement";
import { Currency } from "types";

export interface Campaign {
    id: number,
    agency_brand_id: number,
    name: string,
    description: string,
    status: string,
    created_at: string,
    updated_at: string,
    hero_img_url: string | null,
    uuid: string,
    gender: string,
    budget: string,
    strategy: string,
    agency_id: number,
    brand_id?: number | string,
    is_public?: string,
    goal?: string,
    requirements?: RequirementDetails,
    total_budget?: string | null,
    gallery_images?: string[],
    niche_names?: string[],
    niches?: string[],
    posted_on?: string,
    applications_count: number,
    collaborations_count: number,
    remaining_budget: string | number,
    barter_budget?: string | number,
    barter_reimbursement?: boolean,
    brand: { img_url: string | null, name?: string } | null,
    attach_social_post?: boolean,
    currency: Currency,
    content_media_types: {
        REELS: number,
        FEED: number,
        STORY: number
    },
    [key: string]: unknown,
    landing_page_url?: string,
    mode?: 'offline' | 'workflow',
    platform: string,
    has_transactions?: boolean,
    version?: number,
    tier?: string,
    creator_recommendation_initiated?: boolean,
    creator_recommendation_available?: boolean,
    creator_recommendation_allowed?: boolean,
    fixed_offer?: string,
    application_deadline?: string | null,
    agency: {
        name: string,
        uuid: string
    },
    country: string, // iso code e.g IN, US, etc.
}

export interface CampaignDetail {
    share_url: string,
    campaign: Campaign,
    brand: { img_url: string | null, name?: string, website_url?: string, description?: string }
}

export interface CampaignCreationStep {
    key: string,
    title: string,
    optional?: boolean,
    completed?: boolean,
    url: string,
    sort_order: number,
    [key: string]: unknown
}

export const CAMPAIGN_CREATION_STEPS: CampaignCreationStep[] = [
    { key: 'campaign-info', title: 'Campaign Info', url: '/campaign/<campaignId>/edit', sort_order: 1 },
    { key: 'gigs', title: 'Gigs', url: '/campaign/<campaignId>/gigs', sort_order: 2 },
    { key: 'creators', title: 'Creators', url: '/campaign/<campaignId>/creators', sort_order: 3 },
    { key: 'deliverables', title: 'Deliverables', url: '/campaign/<campaignId>/deliverables', sort_order: 4 },
    { key: 'controls', title: 'Controls', url: '/campaign/<campaignId>/controls', sort_order: 5 },
    { key: 'payment-terms', title: 'Payment Terms', url: '/campaign/<campaignId>/payment-terms', sort_order: 6 },
    { key: 'banner', title: 'Banner', url: '/campaign/<campaignId>/banner', sort_order: 7 },
];

export interface CampaignShort {
    uuid: string,
    name: string,
    hero_img_url: string,
    strategy: string,
    requirement: {
        gender_name?: string,
        follower_count?: number
    },
    requirements: {
        creator_count?: { max?: number },
        [key: string]: any
    }
    created: string,
    active_at: string | null,
    total_application_count: number,
    pending_application_count: number,
    collaboration_count: number,
    reach_count: number,
    plays_count: number,
    interactions_count: number,
    views_count: number,
    impressions_count: number,
    brand: {
        img_url: string | null,
        name?: string
    },
    agency?: {
        name: string
    },
    currency: Currency,
    country: string, // iso code e.g IN, US, etc.
    remaining_budget: string,
    version: number,
    status: string,
    mode: 'offline' | 'workflow',
    budget: string,
    platform: string
    milestones_count: number,
    is_public: 'y' | 'n',
    milestones?: Milestone[]
}

export interface CampaignsResponse {
    count: number,
    items: CampaignShort[],
}

export interface CampaignsFilters {
    page: number,
    status: string,
    q?: string,
    agency_id?: string,
    agency?: { label: string, value: string, id?: number } | null,
    country?: string,
    [key: string]: any
}

export interface CampaignsSlice {
    filters: CampaignsFilters,
    applied_filter_id: string | null,
    filtered_lists: Record<string, {
        count: number,
        list: CampaignShort[],
        page: number,
        last_fetched_at: number | null,
        has_more: boolean,
        expired: boolean,
    }>,
    items: Record<string, {
        data: CampaignDetail,
        last_fetched_at: number | null,
        expired: boolean,
    }>,
    totals: Record<string, {
        last_fetched_at: number | null
        data: {
            [key: string]: number,
        }
    }>,
    loading: boolean,
    error: string | null,
}
